import { Suspense, lazy, useState } from 'react';
import { AddressSearchBox } from './AddressSearchBox';
import { ApiFailure, api } from '../lib/api';
import type { OrderDetail, ZoneRow } from '../lib/types';

// 地图库 250KB —— 只有真的要复核时才加载
const LocationPicker = lazy(() =>
  import('./LocationPicker').then((m) => ({ default: m.LocationPicker })),
);

/**
 * 地址复核。
 *
 * 处理两种情况:
 *   · 建单时没给坐标 → 在地图上点出来,司机才能导航
 *   · 地址本身写错了 → 改地址(仅限取货前)
 *
 * ★ 货已被司机取走后禁止改地址 —— 他正照着旧地址在开,
 *   悄悄改到三公里外不会有任何提示。后端也会拒,这里提前说清楚原因。
 */
export function ReviewAddressDialog({
  order,
  zones,
  onClose,
  onDone,
}: {
  order: OrderDetail;
  zones: ZoneRow[];
  onClose: () => void;
  onDone: () => Promise<void>;
}) {
  const [address, setAddress] = useState(order.receiverAddress);
  const [lat, setLat] = useState<number | null>(order.receiverLat);
  const [lng, setLng] = useState<number | null>(order.receiverLng);
  const [zoneId, setZoneId] = useState(order.zone?.id ?? '');
  const [note, setNote] = useState('');
  /**
   * 坐标是不是**跟着这次改的地址**来的。
   *
   * ★ 这一屏最容易出的错:把地址从 A 街改成 B 街,坐标却还是 A 街那个 ——
   *   单子看起来一切正常,直到司机开到了 A 街。
   *   所以「改了地址但没重新定位」要当场说出来。
   */
  const [coordsFromPick, setCoordsFromPick] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** 改了区之后:运费怎么处理的、记没记到收件人身上 —— 说给人听,再关 */
  const [result, setResult] = useState<{
    price?: 'REPRICED' | 'KEPT_MANUAL' | 'SKIPPED';
    priceNote?: string;
    remembered?: boolean;
  } | null>(null);

  const pickedUp = order.pickedUpAt != null;
  const addressChanged = address.trim() !== order.receiverAddress;
  const coordsChanged = lat !== order.receiverLat || lng !== order.receiverLng;
  const zoneChanged = zoneId !== (order.zone?.id ?? '');
  const dirty = addressChanged || coordsChanged || zoneChanged;

  /**
   * 货已经在司机车上时,服务端会先拒一次并把司机的姓名电话回过来。
   * 这里据此摆一张确认卡 —— 点了「确认修改」才带 confirmInTransit 重发。
   */
  const [confirmInTransit, setConfirmInTransit] = useState<{
    driverName: string | null;
    driverPhone: string | null;
  } | null>(null);

  async function submit(confirmed = false): Promise<void> {
    if (!dirty || busy) return;
    setBusy(true);
    setError(null);
    try {
      const r = await api<NonNullable<typeof result>>(`/orders/${order.id}/review-address`, {
        method: 'POST',
        body: {
          ...(addressChanged ? { receiverAddress: address.trim() } : {}),
          ...(coordsChanged && lat != null && lng != null
            ? { receiverLat: lat, receiverLng: lng }
            : {}),
          ...(zoneChanged && zoneId ? { zoneId } : {}),
          ...(note.trim() ? { note: note.trim() } : {}),
          ...(confirmed ? { confirmInTransit: true } : {}),
        },
      });
      await onDone();
      // 改了区(或地址里的邮编变了):运费和「记住」这两件事要让人看见,别一关了之
      if (r.price || r.remembered !== undefined) setResult(r);
      else onClose();
    } catch (e) {
      /*
       * ★ 按**服务端给的标记**判,不靠匹配错误文案 ——
       *   那句话改一个字这里就失效了。
       */
      const d = e instanceof ApiFailure ? (e.details as Record<string, unknown> | undefined) : undefined;
      if (d?.['requiresConfirm'] === 'inTransit') {
        setConfirmInTransit({
          driverName: (d['driverName'] as string | null) ?? null,
          driverPhone: (d['driverPhone'] as string | null) ?? null,
        });
      } else {
        setError(e instanceof ApiFailure ? e.message : '提交失败');
      }
    } finally {
      setBusy(false);
    }
  }

  if (result) {
    return (
      <div className="fixed inset-0 z-[70] grid place-items-center bg-black/50 p-4" onClick={onClose}>
        <div
          role="dialog"
          aria-modal="true"
          aria-label="地址复核"
          className="w-full max-w-md rounded-2xl bg-white p-5 shadow-2xl dark:bg-slate-800"
          onClick={(e) => e.stopPropagation()}
        >
          <h3 className="mb-3 text-lg font-semibold text-slate-900 dark:text-slate-100">
            {result.remembered === undefined ? '地址已改好' : '区域已改好'}
          </h3>
          <ul className="space-y-1.5 text-sm text-slate-700 dark:text-slate-300" aria-label="复核结果">
            <li>
              {result.price === 'REPRICED'
                ? `运费已按新${result.remembered === undefined ? '地址的邮编' : '区域'}重新算过`
                : result.price === 'KEPT_MANUAL'
                  ? `运费没变:${result.priceNote ?? '这单的运费是人工定的'}`
                  : `运费没重算:${result.priceNote ?? ''}`}
            </li>
            {result.remembered !== undefined && (
              <li>
                {result.remembered
                  ? '已记到这个收件人身上 —— 下次给他建单直接用这个区'
                  : '没找到这个收件人的通讯录记录,这次没记住'}
              </li>
            )}
          </ul>
          <button
            type="button"
            onClick={onClose}
            className="mt-4 h-10 w-full rounded-lg bg-brand-600 text-sm font-medium text-white"
          >
            知道了
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-[70] grid place-items-center bg-black/50 p-4" onClick={onClose}>
      <div
        role="dialog"
        aria-modal="true"
        aria-label="地址复核"
        className="w-full max-w-3xl max-h-[90dvh] overflow-y-auto rounded-2xl bg-white
                   dark:bg-slate-800 p-5 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="mb-1 text-lg font-semibold text-slate-900 dark:text-slate-100">地址复核</h3>
        <p className="mb-4 text-sm text-slate-500">
          {order.orderNo} · {order.receiverName}
        </p>

        {order.needsReview && (
          <div className="mb-3 rounded-lg bg-amber-50 dark:bg-amber-950/40 border
                          border-amber-200 dark:border-amber-900 px-3 py-2 text-sm
                          text-amber-800 dark:text-amber-300">
            这单缺少坐标,司机无法导航,也上不了地图。请在下面的地图上点出准确位置。
          </div>
        )}

        {error && (
          <div className="mb-3 rounded-lg bg-red-50 dark:bg-red-950 border border-red-200
                          dark:border-red-900 px-3 py-2 text-sm text-red-700 dark:text-red-300">
            {error}
          </div>
        )}

        <label className="block mb-3">
          <span className="block mb-1 text-sm text-slate-600 dark:text-slate-400">收货地址</span>
          <textarea
            aria-label="收货地址"
            value={address}
            onChange={(e) => {
              setAddress(e.target.value);
              // 手打过之后,上一次挑的候选就不算数了 —— 坐标未必还对得上
              setCoordsFromPick(false);
            }}
            rows={2}
            className="w-full px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600
                       bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100
                       disabled:opacity-60 disabled:cursor-not-allowed"
          />
          {/*
            ★ 这一屏原来只能**手打地址**,而它恰恰是「地址写错了」的出口 ——
              打完还是没有坐标,人只能再去地图上点一次。
              而挑一条候选就同时把地址和坐标都定下来了,
              这正是复核要的结果。
          */}
          {(
            <AddressSearchBox
              query={address}
              onPick={(c) => {
                setAddress(c.address);
                setLat(c.lat);
                setLng(c.lng);
                setCoordsFromPick(true);
              }}
            />
          )}

          {/*
            ★ 改了地址却没重新定位 —— 当场说出来。
              「B 街的名字配 A 街的坐标」这种单看起来一切正常,
              直到司机开到了 A 街。
              不自动清掉坐标:这一屏的另一半就是地图,
              清掉会和人在地图上点的那一下打架。
          */}
          {addressChanged && !coordsFromPick && (
            <span className="mt-1 block text-xs text-amber-600 dark:text-amber-400">
              地址改了,坐标还是原来那个 —— 在上面挑一条候选,或者在下面的地图上点一下
            </span>
          )}

          {/*
            ★ 取货之后**可以改**了(用户要求:在途才发现地址错是常事)。
              但必须把代价说清楚:他正朝着旧地址开。
              原来这里是禁用输入 + 一句「请取消后重新下单」——
              而取消重下意味着单号变了、司机白跑一趟、货还在他车上。
          */}
          {pickedUp && (
            <span className="mt-1 block text-xs text-amber-600 dark:text-amber-400">
              货已经在司机车上了 —— 他此刻多半正朝着旧地址开。
              改之前先打个电话给他;他手机上那一单也会标出「地址已更新」。
            </span>
          )}
        </label>

        {/*
          ★ 判区打架的单:地址同时命中了普通区和偏远区,规则挑了一个。
            把候选直接摆出来让人点 —— 不然他得自己去下拉里翻,而他要的就是二选一。
        */}
        {(order.zoneCandidates?.length ?? 0) > 0 && (
          <div className="mb-3 rounded-lg border border-amber-200 bg-amber-50 p-3 dark:border-amber-800 dark:bg-amber-950/40">
            <p className="mb-2 text-sm text-amber-900 dark:text-amber-200">
              这个地址同时命中了这几个区(价钱不一样),选对的那个 —— 选完会记到这个收件人身上,下次不用再选:
            </p>
            <div className="flex flex-wrap gap-2" role="group" aria-label="候选区域">
              {order.zoneCandidates!.map((c) => {
                // 优先用手上的区域列表(名字是最新的);列表里没有(刚新建的区)就用建单时存下的那份
                const live = zones.find((x) => x.id === c.zoneId);
                const z = live ?? { id: c.zoneId, code: c.code ?? '', name: c.name ?? '(区域)', isRemote: !!c.isRemote };
                return (
                  <button
                    key={c.zoneId}
                    type="button"
                    onClick={() => setZoneId(z.id)}
                    aria-pressed={zoneId === z.id}
                    className={`rounded-lg border px-3 py-1.5 text-left text-sm ${
                      zoneId === z.id
                        ? 'border-brand-600 bg-brand-600 text-white'
                        : 'border-slate-300 bg-white text-slate-800 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100'
                    }`}
                  >
                    {z.code} {z.name}
                    {z.isRemote && <span className="ml-1 text-xs opacity-80">偏远</span>}
                    <span className="block text-xs opacity-70">命中「{c.keyword}」</span>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        <label className="block mb-3">
          <span className="block mb-1 text-sm text-slate-600 dark:text-slate-400">配送区域</span>
          <select
            aria-label="配送区域"
            value={zoneId}
            onChange={(e) => setZoneId(e.target.value)}
            className="w-full h-10 px-3 rounded-lg border border-slate-300 dark:border-slate-600
                       bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 text-sm"
          >
            <option value="">未匹配</option>
            {zones.map((z) => (
              <option key={z.id} value={z.id}>
                {z.code} {z.name}
              </option>
            ))}
          </select>
        </label>

        <div className="mb-3">
          <div className="mb-1 flex items-baseline justify-between">
            <span className="text-sm text-slate-600 dark:text-slate-400">
              位置(拖拽图钉或点击地图)
            </span>
            <span className="text-xs text-slate-400 tabular-nums">
              {lat != null && lng != null ? `${lat.toFixed(6)}, ${lng.toFixed(6)}` : '尚未设置'}
            </span>
          </div>
          <div className="h-72 rounded-xl overflow-hidden border border-slate-200 dark:border-slate-700">
            <Suspense
              fallback={<div className="h-full grid place-items-center text-slate-400">地图加载中…</div>}
            >
              <LocationPicker
                lat={lat}
                lng={lng}
                onPick={(la, ln) => {
                  setLat(la);
                  setLng(ln);
                }}
              />
            </Suspense>
          </div>
        </div>

        <label className="block mb-4">
          <span className="block mb-1 text-sm text-slate-600 dark:text-slate-400">
            复核说明(选填,会记入时间轴)
          </span>
          <input
            aria-label="复核说明"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="例如:客户来电确认为 B 座侧门"
            className="w-full h-10 px-3 rounded-lg border border-slate-300 dark:border-slate-600
                       bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 text-sm"
          />
        </label>

        {/*
          ★ 「司机已经带着货在路上」的确认卡。
 
            为什么不直接存下去:一次随手编辑就会把一个**正在路上的目的地**
            悄悄换掉,而调度自己都不知道刚刚做了什么。
            这张卡把电话摆在他面前 —— 现实里正确的顺序是先打电话,再改。
        */}
        {confirmInTransit && (
          <div
            role="alertdialog"
            aria-label="司机已取货确认"
            className="mb-3 rounded-xl border border-red-200 bg-red-50 p-3 text-sm
                       dark:border-red-900 dark:bg-red-950"
          >
            <p className="font-medium text-red-800 dark:text-red-200">
              货已经在司机车上了,他此刻多半正朝着旧地址开
            </p>
            <p className="mt-1 text-red-700 dark:text-red-300">
              {confirmInTransit.driverName ?? '司机'}
              {confirmInTransit.driverPhone && (
                <>
                  {' · '}
                  <a className="underline" href={`tel:${confirmInTransit.driverPhone}`}>
                    {confirmInTransit.driverPhone}
                  </a>
                </>
              )}
              {' —— 建议先打个电话。改完他手机上那一单会标「地址已更新」。'}
            </p>
            <div className="mt-2 flex gap-2">
              <button
                onClick={() => setConfirmInTransit(null)}
                className="h-9 flex-1 rounded-lg border border-red-300 text-red-700
                           dark:border-red-800 dark:text-red-300"
              >
                先不改
              </button>
              <button
                onClick={() => {
                  setConfirmInTransit(null);
                  void submit(true);
                }}
                disabled={busy}
                className="h-9 flex-1 rounded-lg bg-red-600 font-medium text-white disabled:opacity-40"
              >
                确认修改
              </button>
            </div>
          </div>
        )}

        <div className="flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 h-10 rounded-lg border border-slate-300 dark:border-slate-600
                       text-slate-700 dark:text-slate-300"
          >
            取消
          </button>
          <button
            onClick={() => void submit()}
            disabled={busy || !dirty}
            className="flex-1 h-10 rounded-lg bg-brand-600 text-white font-medium disabled:opacity-40"
          >
            {busy ? '保存中…' : dirty ? '保存' : '没有改动'}
          </button>
        </div>
      </div>
    </div>
  );
}
